// منوی Console (PRD-PLT §10.2): هر آیتم با Permission پلتفرم؛ نقش‌های مجاز از نگاشت rbac-matrix (access.ts — C-AGENT-200).
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import CableOutlinedIcon from '@mui/icons-material/CableOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import type { MenuItemDefinition } from '@phoenix/ui';
import { rolesFor } from './access';

export interface ConsoleMenuItem extends MenuItemDefinition {
  screenId?: string;
  /** Permission پلتفرم صفحه (platformRoles از آن ساخته می‌شود). */
  platformPermission?: string;
  /** صفحه واقعی دارد (نه placeholder «در دست ساخت»). */
  implemented?: boolean;
}

function item(definition: Omit<ConsoleMenuItem, 'platformRoles'>): ConsoleMenuItem {
  return definition.platformPermission
    ? { ...definition, platformRoles: rolesFor(definition.platformPermission) }
    : definition;
}

export const consoleMenu: readonly ConsoleMenuItem[] = [
  item({ id: 'home', labelKey: 'console:menu.home', path: '/', icon: HomeOutlinedIcon }),
  item({
    id: 'tenants',
    labelKey: 'console:menu.tenants',
    path: '/tenants',
    icon: ApartmentOutlinedIcon,
    platformPermission: 'platform.tenant.view',
    screenId: 'SCR-CONSOLE-001',
    implemented: true,
  }),
  item({
    id: 'plans',
    labelKey: 'console:menu.plans',
    path: '/plans',
    icon: SellOutlinedIcon,
    platformPermission: 'platform.plan.manage',
    screenId: 'SCR-CONSOLE-004',
    implemented: true,
  }),
  item({
    id: 'licenses',
    labelKey: 'console:menu.licenses',
    path: '/licenses',
    icon: VpnKeyOutlinedIcon,
    platformPermission: 'platform.license.issue',
    screenId: 'SCR-CONSOLE-005',
    implemented: true,
  }),
  item({
    id: 'support-access',
    labelKey: 'console:menu.support_access',
    path: '/support-access',
    icon: SupportAgentOutlinedIcon,
    platformPermission: 'platform.support_access.request',
    screenId: 'SCR-CONSOLE-006',
    implemented: true,
  }),
  item({
    id: 'announcements',
    labelKey: 'console:menu.announcements',
    path: '/announcements',
    icon: AnnouncementOutlinedIcon,
    platformPermission: 'platform.announcement.manage',
    screenId: 'SCR-CONSOLE-007',
    implemented: true,
  }),
  // AP-PLT-05: صف تیکت‌های پشتیبانی Phoenix (US-CHN-045؛ C-AGENT-425).
  item({
    id: 'support-tickets',
    labelKey: 'console:menu.support_tickets',
    path: '/support-tickets',
    icon: ContactSupportOutlinedIcon,
    platformPermission: 'platform.support_ticket.handle',
    screenId: 'CONSOLE-SUPPORT-TICKETS',
    implemented: true,
  }),
  item({
    id: 'reference-data',
    labelKey: 'console:menu.reference_data',
    path: '/reference-data',
    icon: LibraryBooksOutlinedIcon,
    platformPermission: 'platform.reference_data.manage',
    screenId: 'SCR-CONSOLE-008',
    implemented: true,
  }),
  // AP-DOC-03 (US-CFG-042): فهرست‌های مقرراتی روی همان مکانیزم داده مرجع.
  item({
    id: 'regulatory-lists',
    labelKey: 'console:menu.regulatory_lists',
    path: '/regulatory-lists',
    icon: GavelOutlinedIcon,
    platformPermission: 'platform.reference_data.manage',
    screenId: 'SCR-CONSOLE-041',
    implemented: true,
  }),
  // AP-CFG-01: تألیف و انتشار بسته‌های تخصصی (US-CFG-030، 031).
  item({
    id: 'specialty-packs',
    labelKey: 'console:menu.specialty_packs',
    path: '/specialty-packs',
    icon: CategoryOutlinedIcon,
    platformPermission: 'platform.cfg.pack.author',
    screenId: 'SCR-CONSOLE-040',
    implemented: true,
  }),
  item({
    id: 'dead-letters',
    labelKey: 'console:menu.dead_letters',
    path: '/dead-letters',
    icon: ErrorOutlineIcon,
    platformPermission: 'platform.dead_letter.view',
    screenId: 'CONSOLE-DEAD-LETTERS',
  }),
  item({
    id: 'audit',
    labelKey: 'console:menu.audit',
    path: '/audit',
    icon: HistoryOutlinedIcon,
    platformPermission: 'platform.audit.view',
    screenId: 'SCR-CONSOLE-009',
    implemented: true,
  }),
  // AP-INT-02: کاتالوگ Adapter، سلامت تجمیعی و اتصال‌های سطح پلتفرم؛ ماتریس انطباق.
  item({
    id: 'integrations',
    labelKey: 'int:menu.console_integrations',
    path: '/integrations',
    icon: CableOutlinedIcon,
    platformPermission: 'platform.int.adapter.manage',
    screenId: 'SCR-CONSOLE-020',
    implemented: true,
  }),
  // AP-INT-10 (US-INT-114، 115): ثبت نرم‌افزار نزد تامین و کاتالوگ نگاشت خطا.
  item({
    id: 'tamin-registration',
    labelKey: 'int:menu.tamin_registration',
    path: '/integrations/tamin',
    icon: VerifiedUserOutlinedIcon,
    platformPermission: 'platform.int.adapter.manage',
    screenId: 'SCR-CONSOLE-022',
    implemented: true,
  }),
  item({
    id: 'compliance',
    labelKey: 'int:menu.compliance',
    path: '/integrations/compliance',
    icon: GavelOutlinedIcon,
    platformPermission: 'platform.int.adapter.manage',
    screenId: 'SCR-CONSOLE-021',
    implemented: true,
  }),
];
